import { useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  CloseButton,
  Divider,
  Drawer,
  Group,
  Loader,
  Menu,
  Select,
  Stack,
  Text,
  Textarea,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import {
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconCheck,
  IconClockPlus,
  IconEdit,
  IconHandGrab,
  IconPhone,
  IconPlaneDeparture,
  IconPlus,
  IconRotateClockwise,
  IconSend,
  IconTicket,
  IconTrash,
  IconUserPlus,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Todo, TodoDueInput } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { parseGermanDate } from '../projects/projectLabels';
import { CompleteTodoModal } from './CompleteTodoModal';
import { AssignedRequestCard } from '../../components/technicianRequests/AssignedRequestCard';
import { TodoFormModal } from './TodoFormModal';
import {
  PRIORITY_MARK,
  RESOLVE_WHEN_LONG,
  TODO_QUERY_KEYS,
  untilLabel,
  customerHref,
  dueColor,
  formatDateTime,
  formatDue,
  formatWhen,
  snoozePresets,
  ticketHref,
} from './todoLabels';
import { useTodoActions } from './useTodoActions';

interface Props {
  todoId: string | null;
  onClose: () => void;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed" w={110} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </Group>
  );
}

/**
 * Verschieben: Schnellauswahl + eigener Termin. `inline` für Popover (Ticket-Cockpit): Menü im
 * selben DOM-Zweig, sonst zählt ein Klick darauf als „außerhalb" und schließt das Popover.
 */
export function SnoozeMenu({
  todo,
  zIndex = 400,
  compact,
  inline,
}: {
  todo: Todo;
  zIndex?: number;
  compact?: boolean;
  inline?: boolean;
}) {
  const { snooze } = useTodoActions();
  const [customOpen, setCustomOpen] = useState(false);
  const [date, setDate] = useState<string | null>(todo.due?.date ?? null);
  const [time, setTime] = useState(todo.due?.time ?? '');
  const pick = (due: TodoDueInput) => snooze.mutate({ id: todo.id, due });

  return (
    <>
      <Menu position="bottom-end" withinPortal={!inline} zIndex={zIndex}>
        <Menu.Target>
          {compact ? (
            <Tooltip label="Verschieben">
              <ActionIcon variant="subtle" color="blue" size="sm" loading={snooze.isPending} aria-label="Verschieben">
                <IconClockPlus size={14} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Button variant="default" leftSection={<IconClockPlus size={16} />} loading={snooze.isPending}>
              Verschieben
            </Button>
          )}
        </Menu.Target>
        <Menu.Dropdown>
          {snoozePresets(todo.due).map((p) => (
            <Menu.Item key={p.key} onClick={() => pick(p.get())}>
              {p.label}
            </Menu.Item>
          ))}
          {!inline && (
            <>
              <Menu.Divider />
              <Menu.Item onClick={() => setCustomOpen(true)}>Anderer Termin …</Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {customOpen && (
        <Drawer.Root opened onClose={() => setCustomOpen(false)} position="bottom" size="auto" zIndex={zIndex + 10}>
          <Drawer.Overlay />
          <Drawer.Content>
            <Drawer.Body p="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Verschieben auf</Text>
                  <CloseButton onClick={() => setCustomOpen(false)} />
                </Group>
                <Group grow>
                  <DateInput
                    dateParser={parseGermanDate}
                    valueFormat="DD.MM.YYYY"
                    placeholder="Datum"
                    value={date}
                    onChange={setDate}
                    popoverProps={{ zIndex: zIndex + 20 }}
                  />
                  <TimeInput placeholder="Uhrzeit (optional)" value={time} onChange={(e) => setTime(e.currentTarget.value)} />
                </Group>
                <Group justify="flex-end">
                  <Button
                    disabled={!date}
                    loading={snooze.isPending}
                    onClick={() => {
                      if (!date) return;
                      pick({ date, time: time || null });
                      setCustomOpen(false);
                    }}
                  >
                    Verschieben
                  </Button>
                </Group>
              </Stack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </>
  );
}

/**
 * Detail einer Aufgabe: Fakten, Aktionen je nach Recht, Abonnenten, Verlauf mit Kommentaren.
 * Öffnen durch den Zuständigen zählt als „angesehen" (macht der Server beim Laden).
 */
export function TodoDrawer({ todoId, onClose }: Props) {
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { completeNow, reopen, remove, extendEscalation, refresh } = useTodoActions();
  const takeOver = useMutation({
    mutationFn: () => api.takeOverTodo(todoId!),
    onSuccess: () => {
      afterChange();
      notifications.show({ title: 'Übernommen', message: 'Die Aufgabe steht jetzt bei dir.', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });
  const [editOpen, setEditOpen] = useState(false);
  const [completing, setCompleting] = useState<Todo | null>(null);
  const [comment, setComment] = useState('');
  const [addSubscriber, setAddSubscriber] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: [...TODO_QUERY_KEYS.all, 'detail', todoId],
    queryFn: () => api.getTodo(todoId!),
    enabled: !!todoId,
  });
  const todo = detail.data?.todo ?? null;
  const events = detail.data?.events ?? [];
  const perms = todo?.permissions;

  const peopleQuery = useQuery({
    queryKey: [...TODO_QUERY_KEYS.all, 'people'],
    queryFn: () => api.getTodoPeople(),
    enabled: !!perms?.canManageSubscribers,
    staleTime: 5 * 60_000,
  });

  const afterChange = () => {
    refresh();
    void queryClient.invalidateQueries({ queryKey: [...TODO_QUERY_KEYS.all, 'detail', todoId] });
  };
  const onError = (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' });

  const sendComment = useMutation({
    mutationFn: () => api.commentTodo(todoId!, comment.trim()),
    onSuccess: () => {
      setComment('');
      afterChange();
    },
    onError,
  });
  const subscribe = useMutation({
    mutationFn: (userId: string) => api.subscribeTodo(todoId!, userId),
    onSuccess: () => {
      setAddSubscriber(null);
      afterChange();
    },
    onError,
  });
  const unsubscribe = useMutation({
    mutationFn: (userId: string) => api.unsubscribeTodo(todoId!, userId),
    onSuccess: afterChange,
    onError,
  });

  const isSubscribed = !!todo?.subscribers.some((s) => s.id === me?.id);
  const done = !!todo?.completedAt;
  const subscriberOptions = (peopleQuery.data?.users ?? [])
    .filter((u) => u.id !== todo?.assignee?.id && !todo?.subscribers.some((s) => s.id === u.id))
    .map((u) => ({ value: u.id, label: u.displayName }));

  const check = () => {
    if (!todo) return;
    if (todo.kind === 'ASSIGNED') setCompleting(todo);
    else completeNow.mutate(todo.id, { onSuccess: afterChange });
  };

  return (
    <Drawer
      opened={!!todoId}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        todo ? (
          <Group gap={6}>
            <Badge variant="light" color={todo.kind === 'ASSIGNED' ? 'blue' : 'gray'}>
              {todo.kind === 'ASSIGNED' ? 'Aufgabe' : 'Erinnerung'}
            </Badge>
            {done && <Badge color="green">erledigt</Badge>}
          </Group>
        ) : (
          'Aufgabe'
        )
      }
      zIndex={250}
    >
      {detail.isLoading && <Loader />}
      {detail.isError && <Alert color="red">{(detail.error as Error).message}</Alert>}
      {todo && perms && (
        <Stack gap="md">
          <Title order={4} td={done ? 'line-through' : undefined}>
            {todo.priority > 0 && (
              <Text span c="orange" fw={700} mr={6}>
                {PRIORITY_MARK[todo.priority]}
              </Text>
            )}
            {todo.title}
          </Title>

          <Group gap="xs">
            {perms.canComplete && (
              <Button color="green" leftSection={<IconCheck size={16} />} onClick={check} loading={completeNow.isPending}>
                Erledigt
              </Button>
            )}
            {perms.canSnooze && <SnoozeMenu todo={todo} zIndex={400} />}
            {perms.canTakeOver && (
              <Button variant="light" color="orange" leftSection={<IconHandGrab size={16} />} loading={takeOver.isPending} onClick={() => takeOver.mutate()}>
                Übernehmen
              </Button>
            )}
            {perms.canReopen && (
              <Button variant="default" leftSection={<IconRotateClockwise size={16} />} loading={reopen.isPending} onClick={() => reopen.mutate(todo.id, { onSuccess: afterChange })}>
                Wieder öffnen
              </Button>
            )}
            {perms.canEdit && (
              <Button variant="default" leftSection={<IconEdit size={16} />} onClick={() => setEditOpen(true)}>
                Bearbeiten
              </Button>
            )}
            {perms.canDelete && (
              <Tooltip label="Löschen">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  loading={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`„${todo.title}" löschen?`)) remove.mutate(todo.id, { onSuccess: onClose });
                  }}
                  aria-label="Löschen"
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          {todo.assigneeAbsence && !done && (
            <Alert color="orange" icon={<IconPlaneDeparture size={16} />} p="xs">
              {todo.assignee?.displayName} ist abwesend {untilLabel(todo.assigneeAbsence.until)}
              {todo.assigneeAbsence.deputy
                ? ` — ${todo.assigneeAbsence.deputy.id === me?.id ? 'du vertrittst' : `Vertretung: ${todo.assigneeAbsence.deputy.displayName}`}.`
                : ' — ohne Vertretung, Meldungen gehen an die Leitung.'}
            </Alert>
          )}

          {todo.escalated && !done && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} p="xs">
              Die Leitung ist informiert, dass diese Aufgabe liegt.
            </Alert>
          )}

          <Stack gap={8}>
            <Fact label="Zuständig">
              <Text size="sm">
                {todo.assignee?.displayName ?? 'niemand (Benutzer gelöscht)'}
                {todo.kind === 'ASSIGNED' && todo.createdBy ? ` · von ${todo.createdBy.displayName}` : ''}
              </Text>
            </Fact>
            <Fact label="Fällig">
              <Group gap={6}>
                <Badge variant="light" color={dueColor(todo)} style={{ textTransform: 'none' }}>
                  {todo.overdue ? 'überfällig · ' : ''}
                  {formatDue(todo.due)}
                </Badge>
                {todo.snoozeCount > 0 && todo.originalDue && (
                  <Text size="xs" c="dimmed">
                    ursprünglich {formatDue(todo.originalDue)} · {todo.snoozeCount}× verschoben
                  </Text>
                )}
              </Group>
            </Fact>
            {todo.kind === 'ASSIGNED' && (
              <Fact label="Eskalation">
                <Group gap={6}>
                  <Text size="sm">
                    {todo.escalateAfterMinutes == null
                      ? 'keine'
                      : todo.escalatesAt
                        ? `Leitung bekommt Bescheid: ${formatDateTime(todo.escalatesAt)}`
                        : 'Leitung informiert'}
                  </Text>
                  {perms.canEdit && !done && (
                    <Tooltip label="Eskalation eine Stufe später">
                      <ActionIcon
                        variant="default"
                        size="sm"
                        loading={extendEscalation.isPending}
                        onClick={() => extendEscalation.mutate(todo.id, { onSuccess: afterChange })}
                        aria-label="Eskalation verlängern"
                      >
                        <IconPlus size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Fact>
            )}
            {todo.kind === 'ASSIGNED' && (
              <Fact label="Angesehen">
                <Text size="sm">{todo.seenAt ? formatWhen(todo.seenAt) : 'noch nicht'}</Text>
              </Fact>
            )}
            {todo.resolveWhen && !done && (
              <Fact label="Nachfassen">
                <Text size="sm">{RESOLVE_WHEN_LONG[todo.resolveWhen]}</Text>
              </Fact>
            )}
            {todo.links
              .filter((l) => l.kind === 'CALL' || l.kind === 'CALLBACK')
              .map((l) => (
                <Fact key={`${l.kind}-${l.refId}`} label={l.kind === 'CALLBACK' ? 'Rückruf' : 'Anruf'}>
                  <Group gap={6} wrap="nowrap">
                    <Anchor href={`tel:${l.meta?.phoneE164 ?? l.refNumber ?? ''}`} size="sm">
                      <Group gap={4} wrap="nowrap">
                        <IconPhone size={14} />
                        <span>{l.label ?? l.refNumber}</span>
                      </Group>
                    </Anchor>
                    {l.kind === 'CALLBACK' && (
                      <Anchor component={Link} to="/admin/callers" size="xs">
                        Rückrufliste
                      </Anchor>
                    )}
                  </Group>
                </Fact>
              ))}
            {todo.links
              .filter((l) => l.kind === 'MATERIAL_REQUEST')
              .map((l) => (
                <AssignedRequestCard key={`${l.kind}-${l.refId}`} requestId={l.refId} />
              ))}
            {todo.links.filter((l) => l.kind !== 'CALL' && l.kind !== 'CALLBACK' && l.kind !== 'MATERIAL_REQUEST').map((l) => (
              <Fact key={`${l.kind}-${l.refId}`} label={l.kind === 'CUSTOMER' ? 'Kunde' : 'Ticket'}>
                <Anchor component={Link} to={l.kind === 'CUSTOMER' ? customerHref(l.refId) : ticketHref(l)} size="sm">
                  <Group gap={4} wrap="nowrap">
                    {l.kind === 'CUSTOMER' ? <IconBuilding size={14} /> : <IconTicket size={14} />}
                    <span>
                      {l.kind === 'CUSTOMER' ? l.label : `${l.refNumber ?? l.refId}${l.label ? ` · ${l.label}` : ''}`}
                    </span>
                  </Group>
                </Anchor>
              </Fact>
            ))}
            {done && (
              <Fact label="Erledigt">
                <Text size="sm">
                  {todo.completedAt ? formatWhen(todo.completedAt) : ''}
                  {todo.completedBy ? ` · ${todo.completedBy.displayName}` : todo.autoCompleted ? ' · von selbst' : ''}
                </Text>
                {todo.completionNote && (
                  <Text size="sm" mt={2}>
                    „{todo.completionNote}"
                  </Text>
                )}
                {todo.followUpTicket && (
                  <Anchor component={Link} to={`/t/${encodeURIComponent(todo.followUpTicket.number ?? todo.followUpTicket.id)}`} size="sm">
                    Folgeticket {todo.followUpTicket.number}
                  </Anchor>
                )}
              </Fact>
            )}
          </Stack>

          {todo.notes && (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {todo.notes}
            </Text>
          )}

          {todo.kind === 'ASSIGNED' && (
            <>
              <Divider label="Abonnenten" labelPosition="left" />
              <Text size="xs" c="dimmed">
                Bekommen Bescheid beim Abhaken und wenn es liegen bleibt — Geschäftsführung, Büro und Teamleitung ohnehin.
              </Text>
              <Group gap={6}>
                {todo.subscribers.length === 0 && (
                  <Text size="sm" c="dimmed">
                    niemand zusätzlich
                  </Text>
                )}
                {todo.subscribers.map((s) => (
                  <Badge
                    key={s.id}
                    variant="light"
                    style={{ textTransform: 'none' }}
                    rightSection={
                      perms.canManageSubscribers || s.id === me?.id ? (
                        <CloseButton size="xs" onClick={() => unsubscribe.mutate(s.id)} aria-label={`${s.displayName} austragen`} />
                      ) : undefined
                    }
                  >
                    {s.displayName}
                  </Badge>
                ))}
              </Group>
              <Group gap="xs">
                {perms.canSubscribeSelf && !isSubscribed && me && (
                  <Button size="xs" variant="light" leftSection={<IconBell size={14} />} loading={subscribe.isPending} onClick={() => subscribe.mutate(me.id)}>
                    Mitverfolgen
                  </Button>
                )}
                {perms.canManageSubscribers && (
                  <Group gap={4} wrap="nowrap">
                    <Select
                      size="xs"
                      placeholder="Person eintragen"
                      data={subscriberOptions}
                      value={addSubscriber}
                      onChange={setAddSubscriber}
                      searchable
                      comboboxProps={{ zIndex: 400 }}
                    />
                    <ActionIcon
                      variant="light"
                      disabled={!addSubscriber}
                      loading={subscribe.isPending}
                      onClick={() => addSubscriber && subscribe.mutate(addSubscriber)}
                      aria-label="Eintragen"
                    >
                      <IconUserPlus size={16} />
                    </ActionIcon>
                  </Group>
                )}
              </Group>
            </>
          )}

          <Divider label="Verlauf" labelPosition="left" />
          <Timeline bulletSize={14} lineWidth={2}>
            {events.map((e) => (
              <Timeline.Item key={e.id} color={e.kind === 'COMMENT' ? 'blue' : e.kind === 'ESCALATED' ? 'red' : 'gray'}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {e.kind === 'COMMENT' ? <b>{e.user?.displayName ?? 'Jemand'}: </b> : null}
                  {e.message}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatWhen(e.createdAt)}
                  {e.kind !== 'COMMENT' && e.user ? ` · ${e.user.displayName}` : e.user ? '' : ' · automatisch'}
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>
          {perms.canComment && (
            <Group align="flex-end" gap="xs" wrap="nowrap">
              <Textarea
                placeholder="Kommentar …"
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                autosize
                minRows={1}
                maxRows={5}
                style={{ flex: 1 }}
                maxLength={2000}
              />
              <ActionIcon
                size="lg"
                variant="filled"
                disabled={!comment.trim()}
                loading={sendComment.isPending}
                onClick={() => sendComment.mutate()}
                aria-label="Kommentar senden"
              >
                <IconSend size={16} />
              </ActionIcon>
            </Group>
          )}
        </Stack>
      )}

      <TodoFormModal opened={editOpen} onClose={() => setEditOpen(false)} editing={todo} onSaved={afterChange} zIndex={400} />
      <CompleteTodoModal todo={completing} onClose={() => setCompleting(null)} onDone={afterChange} zIndex={400} />
    </Drawer>
  );
}
